import { create } from 'zustand';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  limit
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { COLLECTIONS } from '../services/firestore';

const splitDisplayName = (displayName = '') => {
  const parts = String(displayName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

const normalizeRoleValue = (value = '') => String(value || '').trim().toLowerCase();

const toStoredRole = (value = '') => {
  const normalized = normalizeRoleValue(value);
  if (normalized === 'learner') return 'student';
  if (normalized === 'student') return 'student';
  if (normalized === 'teacher') return 'teacher';
  if (normalized === 'tutor') return 'tutor';
  if (normalized === 'official') return 'official';
  if (normalized === 'admin') return 'admin';
  return normalized || 'teacher';
};

const toDisplayUserRole = (value = '') => {
  const normalized = normalizeRoleValue(value);
  if (normalized === 'student' || normalized === 'learner') return 'Learner';
  if (normalized === 'teacher') return 'Teacher';
  if (normalized === 'tutor') return 'Tutor';
  if (normalized === 'official') return 'Official';
  if (normalized === 'admin') return 'Admin';
  return value || 'Teacher';
};

const computeProfileFlags = (profile = {}, authUser = null) => {
  const firstName = String(profile.firstName || '').trim();
  const lastName = String(profile.lastName || '').trim();
  const institutionName = String(profile.institutionName || '').trim();
  const institutionType = String(profile.institutionType || '').trim();
  const userRole = String(profile.userRole || profile.role || '').trim();
  const email = String(profile.email || authUser?.email || '').trim();

  const profileComplete = Boolean(
    firstName &&
    lastName &&
    institutionName &&
    institutionType &&
    userRole &&
    email
  );

  return {
    profileComplete,
    requiresProfileCompletion: !profileComplete
  };
};

const normalizeUserData = (profile = {}, authUser = null) => {
  const displayNameParts = splitDisplayName(
    profile.displayName || authUser?.displayName || ''
  );
  const merged = {
    ...profile,
    uid: profile.uid || authUser?.uid || '',
    email: profile.email || authUser?.email || '',
    photoURL: profile.photoURL || authUser?.photoURL || '',
    displayName:
      profile.displayName ||
      authUser?.displayName ||
      [profile.firstName || displayNameParts.firstName, profile.lastName || displayNameParts.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
  };

  if (!merged.firstName && displayNameParts.firstName) {
    merged.firstName = displayNameParts.firstName;
  }
  if (!merged.lastName && displayNameParts.lastName) {
    merged.lastName = displayNameParts.lastName;
  }
  const canonicalRole = toStoredRole(merged.role || merged.userRole);
  merged.role = canonicalRole;
  merged.userRole = toDisplayUserRole(merged.userRole || canonicalRole);

  return {
    ...merged,
    ...computeProfileFlags(merged, authUser)
  };
};

export const useAuthStore = create((set, get) => ({
  // State
  user: null,
  userData: null,
  isLoading: true,
  isAuthenticated: false,
  
  // Actions
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setUserData: (userData) => set({ userData }),
  setLoading: (isLoading) => set({ isLoading }),

  // Check if a user is registered in sigma-users by email
  findRegisteredUserByEmail: async (email) => {
    if (!email) return null;
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('email', '==', email),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0];
  },

  // Initialize auth listener
  initializeAuth: () => {
    try {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        set({ isLoading: true });

        if (user) {
          try {
            // Get additional user data from Firestore
            const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              const userData = normalizeUserData(userDoc.data(), user);
              set({
                user,
                userData,
                isAuthenticated: true,
                isLoading: false
              });
            } else {
              const registeredDoc = await get().findRegisteredUserByEmail(user.email);
              if (registeredDoc) {
                const registeredData = registeredDoc.data();
                const mergedProfile = normalizeUserData({
                  ...registeredData,
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName || registeredData.displayName || '',
                  photoURL: user.photoURL || registeredData.photoURL || '',
                  updatedAt: serverTimestamp()
                }, user);

                await setDoc(userDocRef, mergedProfile, { merge: true });

                set({
                  user,
                  userData: mergedProfile,
                  isAuthenticated: true,
                  isLoading: false
                });
              } else {
                const providerIds = Array.isArray(user.providerData)
                  ? user.providerData.map((item) => item?.providerId)
                  : [];
                if (providerIds.includes('google.com')) {
                  const googleProfile = normalizeUserData({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || '',
                    photoURL: user.photoURL || '',
                    role: 'teacher',
                    userRole: 'Teacher',
                    status: 'pending',
                    isApproved: false,
                    authProvider: 'google',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                  }, user);

                  await setDoc(userDocRef, googleProfile, { merge: true });

                  set({
                    user,
                    userData: googleProfile,
                    isAuthenticated: true,
                    isLoading: false
                  });
                } else {
                  await signOut(auth);
                  set({
                    user: null,
                    userData: null,
                    isAuthenticated: false,
                    isLoading: false
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            set({ user, userData: null, isAuthenticated: true, isLoading: false });
          }
        } else {
          set({
            user: null,
            userData: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error('Firebase auth initialization failed:', error);
      set({ isLoading: false, isAuthenticated: false });
      return () => {};
    }
  },

  // Sign up
  signUp: async (email, password, profileData) => {
    try {
      set({ isLoading: true });
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update display name
      if (profileData.firstName && profileData.lastName) {
        await updateProfile(user, {
          displayName: `${profileData.firstName} ${profileData.lastName}`
        });
      }
      
      // Create user profile in Firestore
      await get().createUserProfile(user, profileData);
      
      return { success: true, user };
    } catch (error) {
      console.error('Sign up error:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Sign in
  signIn: async (email, password) => {
    try {
      set({ isLoading: true });

      const registeredDoc = await get().findRegisteredUserByEmail(email);
      if (!registeredDoc) {
        throw new Error('No registered account found for this email.');
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Sign in error:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    try {
      set({ isLoading: true });
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        const registeredDoc = await get().findRegisteredUserByEmail(user.email);
        if (registeredDoc) {
          const registeredData = registeredDoc.data();
          const mergedProfile = normalizeUserData({
            ...registeredData,
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || registeredData.displayName || '',
            photoURL: user.photoURL || registeredData.photoURL || '',
            authProvider: 'google',
            updatedAt: serverTimestamp()
          }, user);
          await setDoc(userDocRef, mergedProfile, { merge: true });
        } else {
          const googleProfile = normalizeUserData({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            role: 'teacher',
            userRole: 'Teacher',
            institutionType: '',
            institutionName: '',
            status: 'pending',
            isApproved: false,
            authProvider: 'google',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }, user);
          await setDoc(userDocRef, googleProfile, { merge: true });
        }
      }

      return { success: true, user };
    } catch (error) {
      console.error('Google sign in error:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await signOut(auth);
      set({ 
        user: null, 
        userData: null, 
        isAuthenticated: false 
      });
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },

  // Create user profile in Firestore
  createUserProfile: async (user, additionalData) => {
    try {
      const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
      
      const userData = normalizeUserData({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        isApproved: false,
        status: 'pending',
        role: additionalData.role || 'teacher',
        userRole: additionalData.userRole || additionalData.role || 'teacher',
        authProvider: user.providerData?.[0]?.providerId || 'password',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...additionalData
      }, user);
      
      await setDoc(userDocRef, userData);
      
      set({ 
        user, 
        userData, 
        isAuthenticated: true, 
        isLoading: false 
      });
      
      return userData;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (updates) => {
    try {
      const { user } = get();
      if (!user) throw new Error('No authenticated user');
      
      const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
      const existingDoc = await getDoc(userDocRef);
      const currentUserData = get().userData || {};
      const nextProfile = normalizeUserData({
        ...currentUserData,
        ...updates
      }, user);
      const updateData = {
        ...nextProfile,
        ...updates,
        role: toStoredRole(updates.userRole || updates.role || nextProfile.userRole || nextProfile.role),
        userRole: toDisplayUserRole(updates.userRole || updates.role || nextProfile.userRole || nextProfile.role),
        updatedAt: serverTimestamp()
      };

      if (existingDoc.exists()) {
        await updateDoc(userDocRef, updateData);
      } else {
        const createData = normalizeUserData({
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          isApproved: false,
          status: 'pending',
          role: toStoredRole(updates.userRole || updates.role || 'teacher'),
          userRole: toDisplayUserRole(updates.userRole || updates.role || 'teacher'),
          authProvider: user.providerData?.[0]?.providerId || 'password',
          createdAt: serverTimestamp(),
          ...updateData
        }, user);
        await setDoc(userDocRef, createData, { merge: true });
      }

      if (nextProfile.firstName && nextProfile.lastName) {
        const nextDisplayName = `${nextProfile.firstName} ${nextProfile.lastName}`.trim();
        if (nextDisplayName && nextDisplayName !== user.displayName) {
          await updateProfile(user, { displayName: nextDisplayName });
        }
      }
      
      // Update local state
      set({ 
        userData: {
          ...currentUserData,
          ...nextProfile,
          ...updates,
          role: toStoredRole(updateData.userRole || updateData.role),
          userRole: toDisplayUserRole(updateData.userRole || updateData.role)
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Check if user has role
  hasRole: (role) => {
    const { userData } = get();
    const currentRole = toStoredRole(userData?.role || userData?.userRole);
    if (role === 'admin') {
      return currentRole === 'admin' || userData?.isAdmin === true;
    }
    return currentRole === toStoredRole(role);
  },

  // Check if user is approved
  isApproved: () => {
    const { userData } = get();
    return userData?.isApproved === true || userData?.status === 'approved';
  },

  // Check if user is admin
  isAdmin: () => {
    const { userData } = get();
    return toStoredRole(userData?.role || userData?.userRole) === 'admin' || userData?.isAdmin === true;
  },

  isProfileComplete: () => {
    const { userData } = get();
    return normalizeUserData(userData || {}).profileComplete === true;
  },

  needsProfileCompletion: () => {
    const { userData } = get();
    return normalizeUserData(userData || {}).requiresProfileCompletion === true;
  }
}));
